import type {
    SketchDimensionState,
    SketchPointState,
    SketchSolveStatus,
    SketchSolverIslandSnapshot,
    SolverParameterEntry,
} from '../../../history/solver/SketchSolverRecordTypes';
import type {SketchDimensionSnapshot} from '../../dimension/SketchDimensionTypes';
import {measureSketchDimensionFromSource} from '../../dimension/measureSketchDimensionValue';
import {SketchParametricSystemBuilder} from '../compiler/SketchParametricSystemBuilder';
import type {
    SketchParametricIslandBuildResult,
    SolveSketchParametricIslandInput,
    SolveSketchParametricIslandResult,
} from '../../../../types/sketch/solver/SketchParametricIslandTypes';
import type {
    SketchParametricRankResult,
    SketchParametricSolveResult,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import type {SketchSolvedEntityParameters} from '../../../../types/sketch/solver/SketchParametricRuntimeTypes';
import type {SerializablePlainData} from '../../../history/domain/SerializablePlainData';
import {SKETCH_PARAMETRIC_TOLERANCE} from '../core/SketchParametricToleranceConfig';

/**
 * 校验求解后的直线定义域。
 *
 * 涉及单位方向或投影除法的几何关系在严格零长度上没有定义。即使数值
 * 求解报告收敛，也必须在原子写回前用统一容差拒绝退化终态，避免非法
 * 几何成为下一次交互的基线。
 *
 * @param build 已求解的参数系统。
 * @returns 首个退化直线实体 ID；全部有效时返回 null。
 */
function resolveDegenerateSolvedLineId(build: SketchParametricIslandBuildResult): string | null {
    for (const [entityId, line] of build.linePointHandles) {
        const length = Math.hypot(
            build.system.read(line[1].xId) - build.system.read(line[0].xId),
            build.system.read(line[1].yId) - build.system.read(line[0].yId),
        );

        if (!Number.isFinite(length) || length <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            return entityId;
        }
    }
    return null;
}

/**
 * 按尺寸领域快照测量当前系统的几何业务值（driven 显示值来源，02 计划 §7.4）。
 *
 * 说明：测量分支逻辑收口在 `measureSketchDimensionFromSource`（唯一权威），
 * 本函数只提供 solver 求解系统数据源；运行时数据源见
 * `createSketchRuntimeMeasureSource`（拖拽/几何提交后重测 driven 显示值）。
 *
 * @param dimension 尺寸快照。
 * @param build 构建结果（求解后读取即为最新几何）。
 * @returns 业务测量值（核心单位，幅值）；类型不支持或引用不可解析返回 null。
 */
export function measureSketchDimensionOnSystem(
    dimension: SketchDimensionSnapshot,
    build: SketchParametricIslandBuildResult,
): number | null {
    const system = build.system;

    return measureSketchDimensionFromSource(dimension, {
        readPoint: (index) => {
            const pointId = dimension.references[index]?.pointId;
            const handle = pointId ? build.pointHandles.get(pointId) : undefined;

            return handle ? {x: system.read(handle.xId), y: system.read(handle.yId)} : null;
        },
        readLine: (index) => {
            const entityId = dimension.references[index]?.entityId;
            const handles = entityId ? build.linePointHandles.get(entityId) : undefined;

            if (!handles) {
                return null;
            }

            return {
                start: {x: system.read(handles[0].xId), y: system.read(handles[0].yId)},
                end: {x: system.read(handles[1].xId), y: system.read(handles[1].yId)},
            };
        },
        readRadius: (entityId) => {
            const radiusParameterId = build.radiusParameterIds.get(entityId);

            return radiusParameterId ? system.read(radiusParameterId) : null;
        },
        /** 样条总长 = 初始采样弧长 × 整体缩放系数（弧长对相似变换一次齐次）。 */
        readSplineLength: (entityId) => {
            const splineState = build.splineLengthStates.get(entityId);

            return splineState
                ? splineState.baseLength * system.read(splineState.scaleParameterId)
                : null;
        },
        /** 弧长 = 半径参数 × 扫角（端点/圆心当前解按走向规约反算，与弧长方程同表达式）。 */
        readArcLength: (entityId) => {
            const arcState = build.arcGeometryStates.get(entityId);

            if (!arcState) {
                return null;
            }

            const cx = system.read(arcState.center.xId);
            const cy = system.read(arcState.center.yId);
            const startAngle = Math.atan2(system.read(arcState.start.yId) - cy, system.read(arcState.start.xId) - cx);
            const endAngle = Math.atan2(system.read(arcState.end.yId) - cy, system.read(arcState.end.xId) - cx);
            let sweep = arcState.clockwise ? startAngle - endAngle : endAngle - startAngle;

            sweep %= Math.PI * 2;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }

            return system.read(arcState.radiusParameterId) * sweep;
        },
    });
}

/** 用求解后的参数值重建 island 点状态。 */
function rebuildPointStates(
    island: SketchSolverIslandSnapshot,
    build: SketchParametricIslandBuildResult,
): readonly SketchPointState[] {
    return island.pointStates.map((pointState) => {
        const handle = build.pointHandles.get(pointState.pointId);

        if (!handle) {
            return pointState;
        }

        /**
         * worldPosition 是运行时派生值，参数解只更新局部坐标；
         * 世界坐标由运行时写回后重新采集，避免纯数据层携带过期世界坐标。
         */
        const {worldPosition, ...rest} = pointState;

        void worldPosition;

        return {
            ...rest,
            localPosition: [build.system.read(handle.xId), build.system.read(handle.yId)] as const,
        };
    });
}

/** 用求解后的参数值重建 island 参数向量条目。 */
function rebuildParameterEntries(
    island: SketchSolverIslandSnapshot,
    build: SketchParametricIslandBuildResult,
): readonly SolverParameterEntry[] {
    return island.parameterVector.entries.map((entry) => (
        build.system.hasParameter(entry.parameterId)
            ? {...entry, value: build.system.read(entry.parameterId)}
            : entry
    ));
}

/** 用测量值更新 driven 尺寸状态。 */
function rebuildDimensionStates(
    island: SketchSolverIslandSnapshot,
    measuredValues: ReadonlyMap<string, number>,
): readonly SketchDimensionState[] {
    return island.dimensionStates.map((dimensionState) => {
        const measured = measuredValues.get(dimensionState.dimensionId);

        if (measured === undefined) {
            return dimensionState;
        }

        return dimensionState.drivingState === 'driven'
            ? {...dimensionState, value: measured, measuredValue: measured}
            : {...dimensionState, measuredValue: measured};
    });
}

/**
 * 对 solver island 执行统一参数化求解。
 *
 * 迁移边界（每个 island 只有一个最终写回者）：
 * 1. 本入口同时承载 driving 尺寸与已迁移拖拽意图；一次操作只允许参数系统写回，
 *    禁止再串联对象式 BFS；
 * 2. island 含参数内核暂不支持的关系/尺寸时，直接返回 `applied: false` 并携带
 *    未支持清单——忽略未支持关系强行求解会破坏这些关系，禁止部分写回。
 *
 * @param input 求解输入。
 * @returns 求解输出（纯数据；运行时写回由 `SketchParametricRuntimeApplier` 消费）。
 */
export function solveSketchParametricIsland(
    input: SolveSketchParametricIslandInput,
): SolveSketchParametricIslandResult {
    if (input.shouldCancel?.()) {
        return {
            applied: false,
            island: input.island,
            solveStatus: 'unknown',
            solveResult: null,
            rank: null,
            build: null,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: '参数求解已取消，保持当前几何。',
        };
    }
    let build: SketchParametricIslandBuildResult;

    try {
        build = new SketchParametricSystemBuilder().build({
            island: input.island,
            dimensions: input.dimensions,
            entityIdAliases: input.entityIdAliases,
            operationIntents: input.operationIntents,
            authoritativePointIds: input.authoritativePointIds,
            authoritativeEntityIds: input.authoritativeEntityIds,
        });
    } catch (error) {
        return {
            applied: false,
            island: input.island,
            solveStatus: 'invalid-solution',
            solveResult: null,
            rank: null,
            build: null,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: `island 参数系统构建失败：${error instanceof Error ? error.message : String(error)}`,
        };
    }

    if (build.unsupportedConstraints.length > 0
        || build.unsupportedDimensions.length > 0
        || build.unsupportedOperationIntents.length > 0) {
        const constraintSummary = build.unsupportedConstraints
            .map((item) => `${item.kind}(${item.id})`)
            .join('、');
        const dimensionSummary = build.unsupportedDimensions
            .map((item) => `${item.kind}(${item.id})`)
            .join('、');
        const intentSummary = build.unsupportedOperationIntents
            .map((item) => `${item.kind}(${item.id})`)
            .join('、');

        return {
            applied: false,
            island: input.island,
            solveStatus: 'unknown',
            solveResult: null,
            rank: null,
            build,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: '参数内核暂不支持该 island 的全部约束、尺寸或操作意图，保持当前几何：'
                + `${[constraintSummary, dimensionSummary, intentSummary].filter(Boolean).join('；')}`,
        };
    }

    let solveResult: SketchParametricSolveResult;
    let rank: SketchParametricRankResult;

    try {
        solveResult = build.system.solve({shouldCancel: input.shouldCancel});
        rank = build.system.analyzeRank();
    } catch (error) {
        // 退化几何（零长度方向等）：invalid，不写回。
        return {
            applied: false,
            island: input.island,
            solveStatus: 'invalid-solution',
            solveResult: null,
            rank: null,
            build,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: `求解结果无效：${error instanceof Error ? error.message : String(error)}`,
        };
    }

    if (solveResult.terminationReason === 'cancelled') {
        return {
            applied: false,
            island: input.island,
            solveStatus: 'unknown',
            solveResult,
            rank,
            build,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: '参数求解已取消，保持当前几何。',
        };
    }

    if (!solveResult.converged) {
        return {
            applied: false,
            island: input.island,
            solveStatus: 'no-solution',
            solveResult,
            rank,
            build,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: solveResult.terminationReason === 'blocked'
                ? '直接操作目标被固定关系或权威输入锁定，保持当前几何。'
                : `方程组在当前分支下未收敛，最大容差满足比 ${solveResult.maxResidual}`,
        };
    }

    const degenerateLineId = resolveDegenerateSolvedLineId(build);

    if (degenerateLineId) {
        return {
            applied: false,
            island: input.island,
            solveStatus: 'invalid-solution',
            solveResult,
            rank,
            build,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: `求解结果无效：草图直线 ${degenerateLineId} 退化为零长。`,
        };
    }

    /**
     * 分支提示只能在数值解收敛后读取；读取失败与非法几何同样禁止部分写回，
     * 从而保证实体参数、点坐标和关系领域状态始终属于同一事务。
     */
    const resolvedRelationBranchHints = new Map<string, SerializablePlainData>();

    try {
        build.relationBranchHintReaders.forEach((reader, relationId) => {
            resolvedRelationBranchHints.set(relationId, reader());
        });
    } catch (error) {
        return {
            applied: false,
            island: input.island,
            solveStatus: 'invalid-solution',
            solveResult,
            rank,
            build,
            measuredValues: new Map(),
            solvedEntityParameters: new Map(),
            resolvedRelationBranchHints: new Map(),
            message: `关系分支提示生成失败：${error instanceof Error ? error.message : String(error)}`,
        };
    }

    /**
     * 收集求解后的实体非点参数（当前为圆/弧半径）。
     * radiusParameterIds 同时以持久 ID 与运行时 uuid 为键，两边都登记，
     * 写回侧无论持有哪种身份都能命中。
     */
    const solvedEntityParameters = new Map<string, SketchSolvedEntityParameters>();

    build.radiusParameterIds.forEach((parameterId, entityId) => {
        solvedEntityParameters.set(entityId, {radius: build.system.read(parameterId)});
    });

    const measuredValues = new Map<string, number>();

    (input.dimensions ?? []).forEach((dimension) => {
        const measured = measureSketchDimensionOnSystem(dimension, build);

        if (measured !== null) {
            measuredValues.set(dimension.dimensionId, measured);
        }
    });

    const solveStatus: SketchSolveStatus = rank.degreesOfFreedom === 0 ? 'fully-defined' : 'under-defined';
    const parameterVector = {
        ...input.island.parameterVector,
        entries: rebuildParameterEntries(input.island, build),
    };

    return {
        applied: true,
        island: {
            ...input.island,
            pointStates: rebuildPointStates(input.island, build),
            dimensionStates: rebuildDimensionStates(input.island, measuredValues),
            parameterVector,
            solveStatus,
            lastStableParameterVector: parameterVector,
            diagnostics: {
                /** residual 统一记录最大容差满足比，物理分量保存在 details。 */
                residual: solveResult.maxResidual,
                iterationCount: solveResult.iterations,
                details: {
                    rank: rank.rank,
                    degreesOfFreedom: rank.degreesOfFreedom,
                    freeParameterCount: rank.columnCount,
                    rawLengthResidual: solveResult.rawResidualByUnit?.length ?? null,
                    rawAngleResidual: solveResult.rawResidualByUnit?.angle ?? null,
                    rawDirectionResidual: solveResult.rawResidualByUnit?.direction ?? null,
                    rawDimensionlessResidual: solveResult.rawResidualByUnit?.dimensionless ?? null,
                },
            },
        },
        solveStatus,
        solveResult,
        rank,
        build,
        measuredValues,
        solvedEntityParameters,
        resolvedRelationBranchHints,
    };
}
